import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/common';
import './HelpSupport.css';

export default function HelpSupport() {
  const navigate = useNavigate();
  
  // ✅ FORM STATE
  const [selectedOption, setSelectedOption] = useState(null);
  const [description, setDescription] = useState('');
  const [attachment, setAttachment] = useState(null);
  
  // ✅ MODAL STATE
  const [showModal, setShowModal] = useState(false);
  const [ticketNumber, setTicketNumber] = useState('');
  
  // ✅ HISTORY STATE
  const [showHistory, setShowHistory] = useState(false);
  const [ticketHistory, setTicketHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historyError, setHistoryError] = useState(null);
  
  // ✅ LOADING STATE
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // ✅ SUPPORT OPTIONS
  const supportOptions = [
    'Device not working',
    'POOL positive but device OFF',
    'Refunds related',
    'Withdraw WALLET',
    'Account related',
    'Raise consumption dispute',
    'Other',
  ];

  // ✅ FETCH TICKET HISTORY
  const fetchTicketHistory = async () => {
    try {
      setLoadingHistory(true);
      setHistoryError(null);

      const token =
        localStorage.getItem('authToken') ||
        localStorage.getItem('token') ||
        localStorage.getItem('jwt_token');

      if (!token) {
        throw new Error('Authentication token not found');
      }

      const apiUrl =
        process.env.REACT_APP_API_BASE_URL || 'https://viz-sm-be.onrender.com'

      const response = await fetch(`${apiUrl}/api/support/getTickets`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch tickets');
      }

      setTicketHistory(data.tickets || []);
      setShowHistory(true);
    } catch (err) {
      setHistoryError(
        err instanceof Error ? err.message : 'Failed to load history'
      );
    } finally {
      setLoadingHistory(false);
    }
  };

  // ✅ HANDLE BACK
  const handleBack = () => {
    navigate('/home');
  };

  // ✅ HANDLE OPTION SELECT
  const handleOptionSelect = (option) => {
    setSelectedOption(option);
  };

  // ✅ HANDLE DESCRIPTION CHANGE
  const handleDescriptionChange = (e) => {
    setDescription(e.target.value);
  };

  // ✅ HANDLE ATTACHMENT CHANGE
  const handleAttachmentChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setAttachment(e.target.files[0]);
    }
  };

  // ✅ RAISE TICKET
  const handleRaiseTicket = async () => {
    try {
      if (!selectedOption) {
        alert('Please select an issue type');
        return;
      }

      if (!description.trim() || description.trim().length < 10) {
        alert('Please describe your issue (minimum 10 characters)');
        return;
      }

      setIsSubmitting(true);
      setSubmitError(null);

      const token =
        localStorage.getItem('authToken') ||
        localStorage.getItem('token') ||
        localStorage.getItem('jwt_token');

      if (!token) {
        throw new Error('Authentication token not found');
      }

      const apiUrl =
        process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000'

      const response = await fetch(`${apiUrl}/api/support/raiseTicket`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          issueType: selectedOption,
          description: description.trim(),
          attachmentName: attachment?.name || null,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to raise ticket');
      }

      setTicketNumber(data.ticketNumber);
      setShowModal(true);

      setTimeout(() => {
        setShowModal(false);
        setSelectedOption(null);
        setDescription('');
        setAttachment(null);
        setSubmitError(null);
      }, 3000);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to raise ticket';
      setSubmitError(msg);
      alert(`Error: ${msg}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // ✅ CLOSE HISTORY MODAL
  const closeHistory = () => {
    setShowHistory(false);
    setTicketHistory([]);
    setHistoryError(null);
  };

  // ✅ FORMAT DATE
  const formatDate = (date) => {
    return new Date(date).toLocaleString('en-IN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  // ✅ GET STATUS COLOR
  const getStatusColor = (status) => {
    switch (status) {
      case 'Open':
      case 'Pending':
      case 'In Progress':
        return 'pending';
      case 'Resolved':
      case 'Closed':
        return 'resolved';
      default:
        return 'pending';
    }
  };

  return (
    <div className="help-support-root">
      {/* HEADER */}
      <div className="help-support-header">
        <div className="help-support-header-side">
          <button
            className="help-support-back-btn"
            onClick={handleBack}
            aria-label="Back"
            title="Go back to home"
          >
            <i className="fas fa-arrow-left" />
          </button>
        </div>
        <div className="help-support-header-center">
          <h1 className="help-support-title">Help & Support</h1>
        </div>
        <div className="help-support-header-side">
          <button
            className="help-support-history-btn"
            onClick={fetchTicketHistory}
            aria-label="Ticket History"
            title="View ticket history"
            disabled={loadingHistory}
          >
            <i className="fas fa-history" />
          </button>
        </div>
      </div>

      {/* BODY */}
      <div className="help-support-container">
        <p className="help-support-instruction">
          Select the options you want help with
        </p>

        {/* OPTIONS */}
        <div className="help-support-options">
          {supportOptions.map((option) => (
            <button
              key={option}
              className={`help-support-option-btn ${
                selectedOption === option ? 'selected' : ''
              }`}
              onClick={() => handleOptionSelect(option)}
            >
              {option}
            </button>
          ))}
        </div>

        {submitError && (
          <div
            style={{
              width: '100%',
              maxWidth: '340px',
              backgroundColor: '#ff3333',
              color: 'white',
              padding: '0.8rem',
              borderRadius: '0.6rem',
              fontSize: '0.85rem',
              textAlign: 'center',
              marginTop: '0.5rem',
            }}
          >
            {submitError}
          </div>
        )}

        {/* DESCRIPTION */}
        <div className="help-support-section">
          <label className="help-support-label">Describe your issue</label>
          <textarea
            className="help-support-textarea"
            placeholder="Please describe your issue in detail (minimum 10 characters)"
            value={description}
            onChange={handleDescriptionChange}
            disabled={isSubmitting}
          />
          <small style={{ color: '#999', fontSize: '0.8rem' }}>
            Characters: {description.length}
          </small>
        </div>

        {/* ATTACHMENT */}
        <div className="help-support-attachment">
          <label className="help-support-attachment-label">
            <i className="fas fa-paperclip" />
            Attach file (optional)
          </label>
          <input
            type="file"
            onChange={handleAttachmentChange}
            style={{ display: 'none' }}
            id="attachment-input"
            disabled={isSubmitting}
          />
          <label
            htmlFor="attachment-input"
            style={{
              cursor: 'pointer',
              textDecoration: 'underline',
              color: '#89D957',
              fontSize: '0.85rem',
            }}
          >
            Choose file
          </label>
          {attachment && (
            <p className="help-support-attachment-name">
              <i className="fas fa-check" style={{ color: '#89D957' }} />{' '}
              {attachment.name}
            </p>
          )}
        </div>

        {/* BUTTON */}
        <Button
          onClick={handleRaiseTicket}
          disabled={
            isSubmitting || !selectedOption || description.trim().length < 10
          }
          style={{
            background: isSubmitting ? '#999' : '#0A1F35',
            color: '#FFFFFF',
            fontWeight: 600,
            fontSize: '1rem',
            padding: '0.8rem 2rem',
            borderRadius: '1.8rem',
            border: 'none',
            minHeight: '2.4rem',
            width: '100%',
            maxWidth: '340px',
            marginTop: '1rem',
            cursor: isSubmitting ? 'not-allowed' : 'pointer',
            opacity: isSubmitting ? 0.6 : 1,
            transition: 'all 0.2s ease',
          }}
        >
          {isSubmitting ? 'Raising Ticket...' : 'Raise Ticket'}
        </Button>
      </div>

      {/* SUCCESS MODAL */}
      {showModal && (
        <div className="help-support-modal-overlay">
          <div className="help-support-modal">
            <div className="help-support-modal-icon">
              <i className="fas fa-check-circle" />
            </div>
            <h2 className="help-support-modal-title">Ticket Created</h2>
            <p className="help-support-modal-ticket">Ticket # {ticketNumber}</p>
            <p className="help-support-modal-message">
              Your support ticket has been created successfully. Our team will
              contact you soon.
            </p>
          </div>
        </div>
      )}

      {/* HISTORY MODAL */}
      {showHistory && (
        <div className="help-support-history-overlay" onClick={closeHistory}>
          <div
            className="help-support-history-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="help-support-history-header">
              <h2 className="help-support-history-title">Ticket History</h2>
              <button
                className="help-support-history-close"
                onClick={closeHistory}
                title="Close"
              >
                <i className="fas fa-times" />
              </button>
            </div>

            {loadingHistory && (
              <div
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '2rem',
                }}
              >
                <p>Loading tickets...</p>
              </div>
            )}

            {historyError && (
              <div
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '2rem',
                }}
              >
                <p style={{ color: '#ff3333' }}>{historyError}</p>
              </div>
            )}

            {!loadingHistory &&
              !historyError &&
              ticketHistory.length === 0 && (
                <div
                  style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '2rem',
                  }}
                >
                  <p style={{ color: '#999' }}>No tickets found</p>
                </div>
              )}

            {!loadingHistory &&
              !historyError &&
              ticketHistory.length > 0 && (
                <div className="help-support-history-list">
                  {ticketHistory.map((ticket) => (
                    <div
                      key={ticket._id}
                      className="help-support-ticket-item"
                    >
                      <div className="help-support-ticket-header">
                        <div className="help-support-ticket-info">
                          <p className="help-support-ticket-id">
                            {ticket.ticketNumber}
                          </p>
                          <p className="help-support-ticket-title">
                            {ticket.issueType}
                          </p>
                        </div>
                        <div
                          className={`help-support-ticket-status ${getStatusColor(
                            ticket.status
                          )}`}
                        >
                          {ticket.status}
                        </div>
                      </div>

                      <div className="help-support-timeline">
                        {ticket.timeline &&
                          ticket.timeline.map((event, index) => (
                            <div
                              key={index}
                              className="help-support-timeline-item"
                            >
                              <div className="help-support-timeline-dot" />
                              <div className="help-support-timeline-content">
                                <p className="help-support-timeline-time">
                                  {formatDate(event.timestamp)}
                                </p>
                                <p className="help-support-timeline-status">
                                  {event.status}
                                </p>
                                <p className="help-support-timeline-desc">
                                  {event.description}
                                </p>
                              </div>
                            </div>
                          ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
          </div>
        </div>
      )}
    </div>
  );
}
